import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Table, Column, Cell } from 'fixed-data-table-2';
import { Alert } from 'react-bootstrap';
import * as d3 from 'd3';

import Filter from '../Filter';
import Tooltip from '../Tooltip';

import s from './TermEnrichment.css';

/**
 * Calculates the binomial of (n k)
 */

function binomlog(n, k) {
  let coeff = 0;
  for (let i = n - k + 1; i <= n; i += 1) coeff += Math.log(i);
  for (let i = 1; i <= k; i += 1) coeff -= Math.log(i);
  return coeff;
}

/**
 * Calculates the cumulative hypergeometric function from 0 to k-1
 */

function hyperg(k, K, n, N) {
  let sum = 0;
  for (let i = 0; i < k; i += 1) {
    const logSum = binomlog(K, i) + binomlog(N - K, n - i) - binomlog(N, n);
    sum += Math.exp(logSum);
  }
  return sum;
}

/**
 * Calculates the terms enriched in a set of genes. Only returns terms above
 * the provided cutoff.
 */
function doCalculateEnrichment(cutoff, terms, genes) {
  const termsGenesIntersect = [];
  let termsEnrich = [];
  const databaseGenesIntersect = {};
  const genesSet = new Set(genes.map(gene => gene.id));

  for (let i = 0, len = terms.length; i < len; i += 1) {
    if (!databaseGenesIntersect[terms[i].database.slug])
      databaseGenesIntersect[terms[i].database.slug] = new Set();

    // Store the intersection of genes in the term and the input
    const termGenes = new Set(terms[i].annotations);
    termsGenesIntersect[i] = new Set(
      [...genesSet].filter(g => termGenes.has(g)),
    );

    // Store the intersection of genes by term database
    databaseGenesIntersect[terms[i].database.slug] = new Set([
      ...databaseGenesIntersect[terms[i].database.slug],
      ...termsGenesIntersect[i],
    ]);
  }

  // Calculate nominal p-values for each term
  for (let i = 0, len = termsGenesIntersect.length; i < len; i += 1) {
    // K = term genes in genome
    // k = term genes in 'genes'
    // n = total genes in 'genes'
    // N = total genes in genome

    const K = terms[i].total_term_annotations;
    const k = termsGenesIntersect[i].size;
    const N = terms[i].total_gene_annotations;
    const n = databaseGenesIntersect[terms[i].database.slug].size;

    const pval = 1 - hyperg(k, K, n, N);
    // As a shortcut, we only add terms where the uncorrected p-value is
    // already less than the cutoff
    if (pval < cutoff && k > 1) {
      termsEnrich.push({
        term: terms[i],
        title: terms[i].title,
        name: terms[i].database.name,
        k,
        K,
        n,
        N,
        pval,
        genes: termsGenesIntersect[i],
      });
    }
  }

  /*
   * Apply FDR multiple test correction
   */

  termsEnrich.sort((a, b) => a.pval - b.pval);

  // BH adjusted pvalues = total tests * raw p-value / rank
  const m = termsGenesIntersect.filter(termGenes => termGenes.size > 0).length;
  for (let i = 0; i < termsEnrich.length; i += 1) {
    const test = termsEnrich[i];
    const rank = i + 1;
    test.pval_fdr = (test.pval * m) / rank;
    // Limit p-values
    if (test.pval_fdr < 1e-12) test.pval_fdr = 1e-12;
  }

  // Enforce monotonicity
  let minPvalue = 1;
  for (let i = termsEnrich.length - 1; i >= 0; i -= 1) {
    const test = termsEnrich[i];
    minPvalue = Math.min(test.pval_fdr, minPvalue);
    if (test.pval_fdr > minPvalue) test.pval_fdr = minPvalue;
  }

  termsEnrich = termsEnrich.filter(enrich => enrich.pval_fdr <= cutoff);
  return termsEnrich;
}

function calculateOverlap(terms, genes) {
  const termsEnrich = [];
  const genesSet = new Set(genes.map(gene => gene.id));

  for (let i = 0, len = terms.length; i < len; i += 1) {
    const termGenes = new Set(terms[i].annotations);
    const termsGenesIntersect = new Set(
      [...genesSet].filter(g => termGenes.has(g)),
    );

    if (termsGenesIntersect.size > 0)
      termsEnrich.push({
        term: terms[i],
        title: terms[i].title,
        name: terms[i].database.name,
        genes: termsGenesIntersect,
      });
  }

  termsEnrich.sort((a, b) => b.genes.size - a.genes.size);

  return termsEnrich;
}

const TermEnrichment = ({
  title = null,
  terms,
  selectedGenes,
  geneSets = {},
  calculateEnrichment = true,
  onTermSelect,
  onTermDeselect,
  containerWidth = 540,
}) => {
  const enrich = useMemo(() => {
    if (calculateEnrichment) {
      return doCalculateEnrichment(0.05, terms, selectedGenes);
    }
    return calculateOverlap(terms, selectedGenes);
  }, [calculateEnrichment, terms, selectedGenes]);

  const [selected, setSelected] = useState({});

  const handleTermSelect = useCallback(
    (event, index) => {
      const id = enrich[index].term.identifier;
      const newSelected = { ...selected };

      if (!newSelected[id]) {
        onTermSelect(enrich[index].term);
        newSelected[id] = enrich[index].term;
      } else {
        onTermDeselect(enrich[index].term);
        delete newSelected[id];
      }

      setSelected(newSelected);
    },
    [enrich, onTermSelect, onTermDeselect, selected],
  );

  const enrichmentComponent = useMemo(() => {
    if (terms.length > 0 && enrich.length === 0) {
      return (
        <Alert style={{ textAlign: 'center' }} bsStyle="warning">
          No enrichments detected
        </Alert>
      );
    }
    return (
      <FilteredTermEnrichTable
        data={enrich}
        geneSets={geneSets}
        handleTermSelect={handleTermSelect}
        enrichment={calculateEnrichment}
        width={containerWidth}
        saveFields={['title', 'name', 'pval_fdr', 'genes_count']}
        saveHeaders={['NAME', 'DATABASE', 'PVALUE', 'GENES']}
        saveFile="enrichment"
      />
    );
  }, [
    terms,
    enrich,
    geneSets,
    handleTermSelect,
    calculateEnrichment,
    containerWidth,
  ]);

  return (
    <div>
      {title}
      {enrichmentComponent}
    </div>
  );
};

TermEnrichment.propTypes = {
  title: PropTypes.element,
  terms: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedGenes: PropTypes.arrayOf(PropTypes.object).isRequired,
  geneSets: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    identifier: PropTypes.object,
  }),
  calculateEnrichment: PropTypes.bool,
  onTermSelect: PropTypes.func.isRequired,
  onTermDeselect: PropTypes.func.isRequired,
  containerWidth: PropTypes.number,
};

/**
 * Table cell for displaying term information (title, description, identifier)
 */
const TermCell = ({ rowIndex, data, col, ...props }) => (
  <Cell className={s.link} {...props}>
    {data[rowIndex].term[col]}
  </Cell>
);
TermCell.propTypes = {
  rowIndex: PropTypes.number,
  data: PropTypes.arrayOf(PropTypes.object),
  col: PropTypes.string,
};
TermCell.defaultProps = {
  rowIndex: -1,
  data: [],
  col: -1,
};

/**
 * Table cell for displaying term information (title, description, identifier)
 */
const DatabaseCell = ({ rowIndex, data, col, ...props }) => (
  <Cell className={s.link} {...props}>
    {data[rowIndex].term.database[col]}
  </Cell>
);
DatabaseCell.propTypes = { ...TermCell.propTypes };

/**
 * Table cell for displaying term information (title, description, identifier)
 */
const ExpCell = ({ rowIndex, data, col, ...props }) => (
  <Cell className={s.link} {...props}>
    {data[rowIndex][col] ? data[rowIndex][col].toExponential(1) : 'N/A'}
  </Cell>
);
ExpCell.propTypes = { ...TermCell.propTypes };

/**
 * Table cell for displaying term information (title, description, identifier)
 */
const GeneCell = ({ rowIndex, data, col, ...props }) => (
  <Cell className={s.link} {...props}>
    {data[rowIndex].genes.size}
  </Cell>
);
GeneCell.propTypes = { ...TermCell.propTypes };

/**
 * Table cell for displaying term information (title, description, identifier)
 */
const StarCell = ({ rowIndex, data, col, geneSets, ...props }) => {
  const id = data[rowIndex].term.identifier;
  const color = geneSets[id] ? geneSets[id].color : '#fff';
  const starred = (
    <i
      style={{ color: d3.rgb(color).darker() }}
      className={`fa fa-lg ${geneSets[id] == null ? 'fa-star-o' : 'fa-star'}`}
    />
  );
  return (
    <Cell
      style={{
        background: color,
        borderTop: '0px',
        borderBotton: '0px',
        borderRadius: '6px 0 0 6px',
      }}
      className={s.link}
      {...props}
    >
      <Tooltip text="Highlight genes in the network">{starred}</Tooltip>
    </Cell>
  );
};
StarCell.propTypes = { ...TermCell.propTypes };

const TermEnrichTable = ({
  data,
  geneSets,
  enrichment,
  handleTermSelect,
  width,
}) => {
  let pvalue = null;
  if (enrichment)
    pvalue = (
      <Column
        header={<Cell>P-VALUE</Cell>}
        width={75}
        cell={<ExpCell data={data} col="pval_fdr" />}
      />
    );
  return (
    <Table
      rowHeight={45}
      rowsCount={data.length}
      width={width}
      height={Math.min(14, data.length) * 45 + 30 + 2}
      headerHeight={30}
      onRowClick={handleTermSelect}
    >
      <Column
        header={<Cell />}
        width={30}
        cell={<StarCell data={data} geneSets={geneSets} />}
      />
      <Column
        header={<Cell>NAME</Cell>}
        width={250}
        cell={<TermCell data={data} col="title" />}
        flexGrow={1}
      />
      <Column
        header={<Cell>DATABASE</Cell>}
        width={110}
        cell={<DatabaseCell data={data} col="name" />}
      />
      <Column
        header={<Cell>GENES</Cell>}
        width={55}
        cell={<GeneCell data={data} />}
      />
      {pvalue}
    </Table>
  );
};

TermEnrichTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  geneSets: PropTypes.PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    identifier: PropTypes.object,
  }),
  enrichment: PropTypes.bool,
  handleTermSelect: PropTypes.func,
  width: PropTypes.number,
};

TermEnrichTable.defaultProps = {
  data: [],
  geneSets: {},
  enrichment: true,
  handleTermSelect: null,
  width: 540,
};

const FilteredTermEnrichTable = Filter({
  dataField: 'data',
  fields: ['title', 'name'],
})(TermEnrichTable);

export default withStyles(s)(TermEnrichment);
